import type { GameClass, ResourceType } from '../types';
import { GameState } from './GameState';
import { getResourceIcon, getResourceLabel, getStatIcon } from '../utils/Icons';

export class ClassSelection {
  private gameState: GameState;
  private classes: GameClass[];
  private onClassSelected: () => void;
  private gameName: string;
  
  constructor(
    gameState: GameState,
    classes: GameClass[],
    onClassSelected: () => void,
    gameName: string = 'Choose Your Path'
  ) {
    this.gameState = gameState;
    this.classes = classes;
    this.onClassSelected = onClassSelected;
    this.gameName = gameName;
  }
  
  render(container: HTMLElement): void {
    // Select 3 random classes using Fisher-Yates shuffle
    const shuffled = [...this.classes];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    const selectedClasses = shuffled.slice(0, 3);
    
    const wrapper = document.createElement('div');
    wrapper.style.cssText = `
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-start;
      min-height: 100%;
      padding: 60px 40px 40px 40px;
      gap: 30px;
      position: relative;
    `;
    
    // Title
    const title = document.createElement('h1');
    title.style.cssText = `
      font-size: 48px;
      color: #fff;
      text-align: center;
      text-shadow: 2px 2px 4px rgba(0,0,0,0.5);
      margin-bottom: 20px;
    `;
    title.textContent = this.gameName;
    
    const subtitle = document.createElement('p');
    subtitle.style.cssText = `
      font-size: 18px;
      color: #888;
      text-align: center;
      margin-bottom: 40px;
    `;
    subtitle.textContent = 'Select a class to begin your journey';
    
    // Classes container
    const classesContainer = document.createElement('div');
    classesContainer.style.cssText = `
      display: flex;
      gap: 30px;
      flex-wrap: wrap;
      justify-content: center;
      max-width: 1200px;
    `;
    
    selectedClasses.forEach((gameClass) => {
      const classCard = this.createClassCard(gameClass);
      classesContainer.appendChild(classCard);
    });
    
    wrapper.appendChild(title);
    wrapper.appendChild(subtitle);
    wrapper.appendChild(classesContainer);
    
    // Fullscreen recommendation text in bottom right
    const fullscreenText = document.createElement('div');
    fullscreenText.style.cssText = `
      position: absolute;
      bottom: 20px;
      right: 20px;
      font-size: 18px;
      color: #888;
      font-style: italic;
    `;
    fullscreenText.textContent = 'recommend to play in fullscreen';
    wrapper.appendChild(fullscreenText);
    
    container.appendChild(wrapper);
  }
  
  private createClassCard(gameClass: GameClass): HTMLElement {
    const card = document.createElement('div');
    card.style.cssText = `
      width: 320px;
      background: linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%);
      border: 2px solid rgba(255, 255, 255, 0.1);
      border-radius: 16px;
      padding: 30px;
      cursor: pointer;
      transition: all 0.3s ease;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
      display: flex;
      flex-direction: column;
      gap: 20px;
    `;
    
    // Hover effect
    card.addEventListener('mouseenter', () => {
      card.style.transform = 'translateY(-10px) scale(1.05)';
      card.style.borderColor = 'rgba(255, 255, 255, 0.3)';
      card.style.boxShadow = '0 8px 16px rgba(0, 0, 0, 0.5)';
    });
    
    card.addEventListener('mouseleave', () => {
      card.style.transform = 'translateY(0) scale(1)';
      card.style.borderColor = 'rgba(255, 255, 255, 0.1)';
      card.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.3)';
    });
    
    // Click handler
    card.addEventListener('click', () => {
      this.gameState.selectClass(gameClass);
      this.onClassSelected();
    });
    
    // Class name
    const name = document.createElement('h2');
    name.style.cssText = `
      font-size: 24px;
      color: #fff;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
      padding-bottom: 15px;
    `;
    name.textContent = gameClass.name;
    
    // Description
    const description = document.createElement('p');
    description.style.cssText = `
      font-size: 18px;
      color: #ccc;
      line-height: 1.6;
      flex: 1;
    `;
    description.textContent = gameClass.description;
    
    // Starting skills
    const skills = document.createElement('div');
    skills.style.cssText = `
      margin-top: 10px;
      padding-top: 15px;
      border-top: 1px solid rgba(255, 255, 255, 0.1);
    `;
    
    const skillsTitle = document.createElement('div');
    skillsTitle.style.cssText = `
      font-size: 14px;
      color: #888;
      margin-bottom: 10px;
      font-weight: bold;
    `;
    skillsTitle.textContent = 'Starting Skills:';
    skills.appendChild(skillsTitle);
    
    const skillsList = document.createElement('div');
    skillsList.style.cssText = `
      display: flex;
      gap: 15px;
      flex-wrap: wrap;
    `;
    
    const hasAnySkills = gameClass.starting_skills.smarts > 0 || 
                         gameClass.starting_skills.tenacity > 0 || 
                         gameClass.starting_skills.social > 0;
    
    if (gameClass.starting_skills.smarts > 0) {
      skillsList.appendChild(this.createSkillDisplay(getStatIcon('smarts'), 'Smarts', gameClass.starting_skills.smarts));
    }
    if (gameClass.starting_skills.tenacity > 0) {
      skillsList.appendChild(this.createSkillDisplay('👁️', 'Tenacity', gameClass.starting_skills.tenacity));
    }
    if (gameClass.starting_skills.social > 0) {
      skillsList.appendChild(this.createSkillDisplay('💬', 'Social', gameClass.starting_skills.social));
    }
    
    if (!hasAnySkills) {
      const noSkills = document.createElement('div');
      noSkills.style.cssText = `
        font-size: 16px;
        color: #666;
        font-style: italic;
      `;
      noSkills.textContent = 'None';
      skillsList.appendChild(noSkills);
    }
    
    skills.appendChild(skillsList);
    
    // Bonus resources
    const bonuses = document.createElement('div');
    bonuses.style.cssText = `
      margin-top: 10px;
      padding-top: 15px;
      border-top: 1px solid rgba(255, 255, 255, 0.1);
    `;
    
    const bonusesTitle = document.createElement('div');
    bonusesTitle.style.cssText = `
      font-size: 14px;
      color: #888;
      margin-bottom: 10px;
      font-weight: bold;
    `;
    bonusesTitle.textContent = 'Bonus Resources:';
    bonuses.appendChild(bonusesTitle);
    
    const bonusesList = document.createElement('div');
    bonusesList.style.cssText = `
      display: flex;
      flex-wrap: wrap;
      font-size: 14px;
      color: #4aff9f;
      align-items: center;
    `;
    
    const bonusResources = gameClass.starting_bonus_resources || {};
    const bonusEntries: HTMLElement[] = [];
    
    const addBonusResource = (resource: ResourceType, value: number) => {
      const icon = getResourceIcon(resource);
      const label = getResourceLabel(resource);
      const span = document.createElement('span');
      
      // Bad Habits is a negative resource, so colors are flipped
      // Red for positive (bad), green for negative (good)
      const isBadHabit = resource === 'bad_habit';
      const color = isBadHabit ? '#ff4a4a' : '#4aff9f'; // Red for bad_habit, green for others
      
      span.style.cssText = `display: inline-flex; align-items: center; gap: 4px; color: ${color};`;
      if (icon) {
        span.innerHTML = `<strong>${label}: +${value}</strong> ${icon}`;
      } else {
        span.textContent = `${label}: +${value}`;
      }
      bonusEntries.push(span);
    };
    
    if (bonusResources.money) addBonusResource('money', bonusResources.money);
    if (bonusResources.food) addBonusResource('food', bonusResources.food);
    if (bonusResources.motivation) addBonusResource('motivation', bonusResources.motivation);
    if (bonusResources.memory) addBonusResource('memory', bonusResources.memory);
    if (bonusResources.connections) addBonusResource('connections', bonusResources.connections);
    if (bonusResources.health) addBonusResource('health', bonusResources.health);
    if (bonusResources.bad_habit) addBonusResource('bad_habit', bonusResources.bad_habit);
    if (bonusResources.sanity) addBonusResource('sanity', bonusResources.sanity);
    if (bonusResources.dignity) addBonusResource('dignity', bonusResources.dignity);
    if (bonusResources.inspiration) {
      const span = document.createElement('span');
      span.style.cssText = 'display: inline-flex; align-items: center; gap: 4px;';
      span.innerHTML = `<strong>Inspiration: +${bonusResources.inspiration}</strong> 💡`;
      bonusEntries.push(span);
    }
    
    if (bonusEntries.length > 0) {
      bonusEntries.forEach((elem, index) => {
        if (index > 0) {
          const separator = document.createElement('span');
          separator.style.cssText = 'color: #888; margin: 0 2px;';
          separator.textContent = ',';
          bonusesList.appendChild(separator);
        }
        bonusesList.appendChild(elem);
      });
    } else {
      const noneText = document.createElement('span');
      noneText.textContent = 'None';
      noneText.style.color = '#666';
      noneText.style.fontStyle = 'italic';
      bonusesList.appendChild(noneText);
    }
    bonuses.appendChild(bonusesList);
    
    card.appendChild(name);
    card.appendChild(description);
    card.appendChild(skills);
    card.appendChild(bonuses);
    
    return card;
  }
  
  private createSkillDisplay(symbol: string, label: string, value: number): HTMLElement {
    const container = document.createElement('div');
    container.style.cssText = `
      display: flex;
      align-items: center;
      gap: 5px;
      font-size: 12px;
      color: #fff;
    `;
    
    const symbolElem = document.createElement('span');
    symbolElem.textContent = symbol;
    
    const text = document.createElement('span');
    text.textContent = `${label}: ${value}`;
    
    container.appendChild(symbolElem);
    container.appendChild(text);
    return container;
  }
}

